import React from 'react'
import { createRoute, AnyRoute, Outlet, useParams } from '@tanstack/react-router'

import { sanitizeSearchParams } from '@shared/lib/url-search-utils'
import {
  isSafeEsignReturnId,
  parseImmigrationEsignReturnSearch,
} from '@shared/lib/e-sign-return-navigation'
import { applicationListSearchSchema } from '../features/application/list/application-list-search-schema'
import { IMMIGRATION_ROUTES } from '../constants/routes'

// Lazy-loaded immigration route components (code-splitting)
const LazyImmigrationLayout = React.lazy(() => import('../features/layout'))
const LazyImmigrationProtectedLayout = React.lazy(() => import('../features/layout/protected'))
const LazyImmigrationApplication = React.lazy(() => import('../features/application'))
const LazyCreateApplication = React.lazy(() => import('../features/application/create'))
const LazyStaffWorkload = React.lazy(() => import('../features/staff-workload'))
const LazyApplicationDetailsPage = React.lazy(() => import('../features/application/details'))
const LazyApplicationDocumentsPage = React.lazy(() => import('../features/application/documents'))
const LazyInternalDocumentsPage = React.lazy(
  () => import('../features/application/documents/internal-documents')
)
const LazyStaffPage = React.lazy(() => import('../features/staff'))
const LazyImmigrationLoginPage = React.lazy(() => import('../features/auth/login'))
const LazyForgotPasswordPage = React.lazy(() => import('../features/auth/forgot-password'))
const LazyResetPasswordPage = React.lazy(() => import('../features/auth/reset-password'))
const LazyConfigurationPage = React.lazy(() => import('../features/configuration'))
const LazyCaseTypesPage = React.lazy(() => import('../features/configuration/case-types'))
const LazyCaseTypeDetailPage = React.lazy(() => import('../features/configuration/detail'))
const LazyReopenApprovalPage = React.lazy(
  () => import('../features/application/details/approval/reopen-approval-page')
)
const LazyImmigrationESign = React.lazy(() => import('../features/e-sign'))
const LazyChangePasswordPage = React.lazy(() => import('../features/change-password'))
const LazyChangePasswordVerifyPage = React.lazy(() => import('../features/change-password/verify'))
const LazyImmigrationChangePasswordVerifyLayout = React.lazy(() =>
  import('../features/change-password/verify/layout')
)
const LazyClientInvoicePage = React.lazy(() => import('../features/application/invoices'))
const LazyVietCanAuditLogPage = React.lazy(() => import('../features/audit-log'))

export const IMMIGRATION_DOCUMENT_TITLE = 'VietCan Immigration'
export const IMMIGRATION_DOCUMENT_DESCRIPTION = 'VietCan Immigration'


const ImmigrationDocumentHead = () => {
  React.useLayoutEffect(() => {
    const previousTitle = document.title
    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute('content') ?? null

    document.title = IMMIGRATION_DOCUMENT_TITLE
    if (meta) meta.setAttribute('content', IMMIGRATION_DOCUMENT_DESCRIPTION)

    return () => {
      document.title = previousTitle
      if (meta && previousDescription !== null) {
        meta.setAttribute('content', previousDescription)
      }
    }
  }, [])
  return null
}

export const createImmigrationRoutes = <T extends AnyRoute>(rootRoute: T) => {
  const immigrationRoute = createRoute({
    path: IMMIGRATION_ROUTES.BASE.slice(1),
    getParentRoute: () => rootRoute,
    component: () => (
      <>
        <ImmigrationDocumentHead />
        <Outlet />
      </>
    ),

    head: () => ({
      meta: [
        {
          name: 'description',
          content: IMMIGRATION_DOCUMENT_DESCRIPTION,
        },
      ],
      title: IMMIGRATION_DOCUMENT_TITLE,
    }),
  })

  const immigrationLoginRoute = createRoute({
    path: 'login',
    getParentRoute: () => immigrationRoute,
    component: () => <LazyImmigrationLoginPage />,
  })

  const immigrationForgotPasswordRoute = createRoute({
    path: 'forgot-password',
    getParentRoute: () => immigrationRoute,
    component: () => <LazyForgotPasswordPage />,
  })

  const immigrationResetPasswordRoute = createRoute({
    path: 'reset-password',
    getParentRoute: () => immigrationRoute,
    component: () => <LazyResetPasswordPage />,
  })

  const immigrationProtectedRoute = createRoute({
    path: 'application',
    getParentRoute: () => immigrationRoute,
    component: () => (
      <LazyImmigrationProtectedLayout>
        <LazyImmigrationLayout />
      </LazyImmigrationProtectedLayout>
    ),
  })

  // Staff workload route (separate protected route)
  const staffWorkloadProtectedRoute = createRoute({
    path: 'staff-workload',
    getParentRoute: () => immigrationRoute,
    component: () => (
      <LazyImmigrationProtectedLayout>
        <LazyImmigrationLayout />
      </LazyImmigrationProtectedLayout>
    ),
  })

  const eSignatureProtectedRoute = createRoute({
    path: 'e-signature',
    getParentRoute: () => immigrationRoute,
    component: () => (
      <LazyImmigrationProtectedLayout>
        <LazyImmigrationLayout />
      </LazyImmigrationProtectedLayout>
    ),
  })

  const changePasswordProtectedRoute = createRoute({
    path: 'change-password',
    getParentRoute: () => immigrationRoute,
    component: () => (
      <LazyImmigrationProtectedLayout>
        <LazyImmigrationLayout />
      </LazyImmigrationProtectedLayout>
    ),
  })

  const staffProtectedRoute = createRoute({
    path: 'staff-management',
    getParentRoute: () => immigrationRoute,
    component: () => (
      <LazyImmigrationProtectedLayout>
        <LazyImmigrationLayout />
      </LazyImmigrationProtectedLayout>
    ),
  })

  // Configuration route (admin only, separate protected route)
  const configurationProtectedRoute = createRoute({
    path: 'configuration',
    getParentRoute: () => immigrationRoute,
    component: () => (
      <LazyImmigrationProtectedLayout>
        <LazyImmigrationLayout />
      </LazyImmigrationProtectedLayout>
    ),
  })

  const auditLogProtectedRoute = createRoute({
    path: 'audit-log',
    getParentRoute: () => immigrationRoute,
    component: () => (
      <LazyImmigrationProtectedLayout>
        <LazyImmigrationLayout />
      </LazyImmigrationProtectedLayout>
    ),
  })

  // Invoices route (separate protected route)
  const invoicesProtectedRoute = createRoute({
    path: 'invoices',
    getParentRoute: () => immigrationRoute,
    component: () => (
      <LazyImmigrationProtectedLayout>
        <LazyImmigrationLayout />
      </LazyImmigrationProtectedLayout>
    ),
  })

  // Application list route (index)
  const immigrationApplicationRoute = createRoute({
    path: '/',
    getParentRoute: () => immigrationProtectedRoute,
    validateSearch: (search: Record<string, unknown>) => {
      const sanitized = sanitizeSearchParams(search)
      const result = applicationListSearchSchema.safeParse(sanitized)
      if (result.success) {
        const validated = { ...result.data }
        const safePage = Number.isFinite(validated.page) && validated.page ? validated.page : 1
        if (safePage === 1) {
          delete (validated as { page?: number }).page
        } else {
          (validated as { page?: number }).page = safePage
        }
        return validated
      }
      return {}
    },
    component: () => <LazyImmigrationApplication />,
  })

  const createApplicationRoute = createRoute({
    path: 'create',
    getParentRoute: () => immigrationProtectedRoute,
    component: () => <LazyCreateApplication />,
  })

  // Staff workload route (index)
  const staffWorkloadRoute = createRoute({
    path: '/',
    getParentRoute: () => staffWorkloadProtectedRoute,
    component: () => <LazyStaffWorkload />,
  })

  // Staff management route (index)
  const staffRoute = createRoute({
    path: '/',
    getParentRoute: () => staffProtectedRoute,
    component: () => <LazyStaffPage />,
  })

  const eSignatureRoute = createRoute({
    path: '/',
    getParentRoute: () => eSignatureProtectedRoute,
    validateSearch: (search: Record<string, unknown>) =>
      parseImmigrationEsignReturnSearch(sanitizeSearchParams(search) as Record<string, unknown>),
    component: () => <LazyImmigrationESign />,
  })

  const changePasswordRoute = createRoute({
    path: '/',
    getParentRoute: () => changePasswordProtectedRoute,
    component: () => <LazyChangePasswordPage />,
  })

  const changePasswordVerifyRoute = createRoute({
    getParentRoute: () => immigrationRoute,
    path: 'change-password/verify',
    component: () => (
      <LazyImmigrationChangePasswordVerifyLayout>
        <LazyChangePasswordVerifyPage />
      </LazyImmigrationChangePasswordVerifyLayout>
    ),
  })

  // Configuration route (index)
  const configurationRoute = createRoute({
    path: '/',
    getParentRoute: () => configurationProtectedRoute,
    component: () => <LazyConfigurationPage />,
  })

  // Configuration case types route
  const configurationCaseTypesRoute = createRoute({
    path: '$categoryId',
    getParentRoute: () => configurationProtectedRoute,
    component: () => <LazyCaseTypesPage />,
  })

  // Configuration detail route (nested under case types)
  const configurationDetailRoute = createRoute({
    path: '$categoryId/$caseTypeId',
    getParentRoute: () => configurationProtectedRoute,
    component: () => <LazyCaseTypeDetailPage />,
  })

  // Invoices route (index)
  const invoicesRoute = createRoute({
    path: '/',
    getParentRoute: () => invoicesProtectedRoute,
    component: () => <LazyClientInvoicePage />,
  })

  const auditLogRoute = createRoute({
    path: '/',
    getParentRoute: () => auditLogProtectedRoute,
    component: () => <LazyVietCanAuditLogPage />,
  })

  const reopenApprovalRoute = createRoute({
    path: 'cases/reopen-approval',
    getParentRoute: () => immigrationRoute,
    component: () => <LazyReopenApprovalPage />,
  })

  const applicationDetailsRoute = createRoute({
    path: '$caseId',
    getParentRoute: () => immigrationProtectedRoute,
    component: function ApplicationDetailsWrapper() {
      const params = useParams({ strict: false })
      return <LazyApplicationDetailsPage caseId={params.caseId ?? ''} />
    },
  })

  const applicationDocumentsRoute = createRoute({
    path: '$caseId/documents',
    getParentRoute: () => immigrationProtectedRoute,
    validateSearch: (search: Record<string, unknown>) => {
      const s = sanitizeSearchParams(search)
      const out: { chat?: string; fileId?: string; documentId?: string } = {}
      if (s.chat === 'true') {
        out.chat = 'true'
      }
      if (s.fileId && isSafeEsignReturnId(s.fileId)) {
        out.fileId = s.fileId.trim()
      }
      if (s.documentId && isSafeEsignReturnId(s.documentId)) {
        out.documentId = s.documentId.trim()
      }
      return out
    },
    component: () => <LazyApplicationDocumentsPage />,
  })

  const applicationInternalDocumentsRoute = createRoute({
    path: '$caseId/internal-documents',
    getParentRoute: () => immigrationProtectedRoute,
    component: () => <LazyInternalDocumentsPage />,
  })

  const routeTree = immigrationRoute.addChildren([
    immigrationLoginRoute,
    immigrationForgotPasswordRoute,
    immigrationResetPasswordRoute,
    immigrationProtectedRoute.addChildren([
      immigrationApplicationRoute,
      createApplicationRoute,
      applicationDetailsRoute,
      applicationDocumentsRoute,
      applicationInternalDocumentsRoute,
    ]),
    reopenApprovalRoute,
    staffWorkloadProtectedRoute.addChildren([staffWorkloadRoute]),
    eSignatureProtectedRoute.addChildren([eSignatureRoute]),
    changePasswordProtectedRoute.addChildren([changePasswordRoute]),
    staffProtectedRoute.addChildren([staffRoute]),
    configurationProtectedRoute.addChildren([
      configurationRoute,
      configurationCaseTypesRoute,
      configurationDetailRoute,
    ]),
    invoicesProtectedRoute.addChildren([invoicesRoute]),
    auditLogProtectedRoute.addChildren([auditLogRoute]),
    changePasswordVerifyRoute,
  ])

  return {
    immigrationRoute,
    routeTree,
  }
}
